import { DehydratedState } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useMount } from 'react-use';
import { hashParamsParse, qsParse, qsStringify } from 'utils';
import { fetchShopPageV2Lander } from '~/api/facetSearch';
import { LinkModuleLink } from '~/app/api/link-modules/route';
import Layout from '~/components/Layout';
import LinkedFiltersList from '~/components/Search/LinkedFiltersList';
import ResultsGridBlock from '~/components/Search/ResultsGridBlock';
import SearchMetadata from '~/components/Search/SearchMetadata';
import SearchPageHeader from '~/components/Search/SearchPageHeader';
import SupplementalCategoryPicker from '~/components/Search/SupplementalCategoryPicker';
import TrendingSearches from '~/components/Search/TrendingSearches';
import PageView from '~/components/legos/PageView';
import { BrowseItemsContext } from '~/contexts/BrowseItemsContext';
import { PageViewDataContext } from '~/contexts/PageViewDataContext';
import { UserSearchFilters } from '~/contexts/UserSearchFilters';
import { FacetFilteringProvider } from '~/contexts/facetFiltering/FacetFilteringContext';
import ErrorPage from '~/pages/_error';
import { CustomNextPage } from '~/typings/custom-next';
import { RailsSortConfig } from '~/typings/services/rails/configuration';
import { FilterState, RailsFacetGroup } from '~/typings/services/rails/facets';
import { RailsItem, RailsThumbnailItem } from '~/typings/services/rails/item';
import { RailsLander } from '~/typings/services/rails/landers';
import { RailsPaging } from '~/typings/services/rails/paging';
import { RailsPageView } from '~/typings/services/rails/tile';
import { BaseUserSearchFilterResponse } from '~/typings/services/rails/userSearchFilters';

interface FullQuery extends ParsedUrlQuery {
  lander: string[];
}

export interface ShopLanderPageProps {
  facets: RailsFacetGroup[];
  filterState: FilterState;
  items: RailsThumbnailItem[];
  lander: RailsLander;
  paging: RailsPaging;
  similarToItem?: RailsItem;
  sorts?: RailsSortConfig;

  pageView: RailsPageView | null;
  pageViewData: any[] | null;

  userSearchFilters?: BaseUserSearchFilterResponse;

  notFoundMetadata?: {
    title: string;
    originalQuery: any; // for saved search
    url: string;
  };

  dehydratedState?: DehydratedState;

  trendingSearches?: LinkModuleLink[];
}

const ShopLanderPage: CustomNextPage<
  ShopLanderPageProps | { notFound: boolean }
> = props => {
  const router = useRouter();
  useMount(() => {
    if (window.location.hash && !('notFound' in props)) {
      const parsedHash = hashParamsParse(window.location.hash);

      // Check if the hash contains values, otherwise it's just a positional anchor
      if (Object.values(parsedHash).filter(Boolean).length == 0) {
        return;
      }

      const split = router.asPath?.split('?');
      const mergedQuery = {
        ...qsParse(split?.[1]),
        ...parsedHash,
      };
      history.replaceState(
        '',
        document.title,
        window.location.pathname + qsStringify(mergedQuery),
      );
      router.reload();
    }
  });

  if ('notFound' in props) {
    return <ErrorPage title="404 - Not found" />;
  }

  const {
    facets,
    filterState,
    items,
    lander,
    notFoundMetadata,
    paging,
    pageView,
    pageViewData,
    similarToItem,
    sorts,
    userSearchFilters,
    trendingSearches,
  } = props;

  return (
    <Layout
      container={false}
      meta={{
        canonical: false,
      }}
    >
      <BrowseItemsContext value={{ items: items, paging: paging }}>
        <UserSearchFilters.Provider
          value={{ filters: userSearchFilters || [] }}
        >
          <FacetFilteringProvider
            facets={facets}
            filterState={filterState}
            lander={lander}
            paging={paging}
            similarToItem={similarToItem}
            sorts={sorts}
            notFoundMetadata={notFoundMetadata}
            type="browse"
          >
            {pageView && !filterState.stid ? (
              <PageViewDataContext value={{ data: pageViewData, pageView }}>
                <SearchMetadata />
                <SearchPageHeader pageView={pageView} />
                <PageView
                  pageView={pageView}
                  ignoreBlockLayouts={['text-pills']}
                  maxPages={50}
                />
                {trendingSearches ? (
                  <TrendingSearches links={trendingSearches} />
                ) : (
                  <LinkedFiltersList />
                )}
                <SupplementalCategoryPicker pageView={pageView} />
              </PageViewDataContext>
            ) : (
              <>
                <SearchMetadata />
                <SearchPageHeader pageView={pageView} />
                <ResultsGridBlock maxPages={50} />
              </>
            )}
          </FacetFilteringProvider>
        </UserSearchFilters.Provider>
      </BrowseItemsContext>
    </Layout>
  );
};

ShopLanderPage.getInitialProps = fetchShopPageV2Lander;

export default ShopLanderPage;
